import { axiosInstance } from "../../Authentication/AxiosInterceptor";
import {
  CreateFileLinksResult,
  LibraryFile,
  LibraryFileCollection,
  LibraryFolder,
  LibraryLink,
  LibraryPortfolio,
} from "../../Types/Library/Library";

function normalizeBlobUrl(data: unknown): string {
  if (typeof data === "string") {
    return data.trim().replace(/^['"]|['"]$/g, "");
  }
  // Legacy endpoints may still return a JSON-encoded string body.
  if (data != null && typeof data === "object") {
    const value = Object.values(data)[0];
    if (typeof value === "string") {
      return value.trim().replace(/^['"]|['"]$/g, "");
    }
  }
  throw new Error("Unexpected response from GetLibraryFileNew");
}

export async function getLibraryFileUrl(
  clientId: string,
  portfolioId: string,
  folderId: string,
  fileName: string
): Promise<string> {
  // Match gen2 / Blazor: fileName in the path. The query-param route
  // (GetLibraryFileNewByQuery) is often missing or misconfigured in APIM.
  const response = await axiosInstance.get(
    `GetLibraryFileNew/${clientId}/${portfolioId}/${folderId}/${encodeURIComponent(fileName)}`
  );
  return normalizeBlobUrl(response.data);
}

/** Resolve a library (or conversation) blob URL from a Cosmos file id alone. */
export async function getLibraryFileUrlByFileId(
  clientId: string,
  fileId: string
): Promise<string> {
  const response = await axiosInstance.get(
    `GetLibraryFileUrlByFileId/${clientId}/${fileId}`
  );
  return normalizeBlobUrl(response.data);
}

export async function fetchLibraryFileBytes(
  clientId: string,
  fileId: string,
  download = false
): Promise<ArrayBuffer> {
  const response = await axiosInstance.get<ArrayBuffer>(
    `GetLibraryFileContentById/${clientId}/${fileId}`,
    {
      params: download ? { download: "1" } : undefined,
      responseType: "arraybuffer",
    }
  );
  return response.data;
}

function normalizeLibraryFile(file: Record<string, unknown>): LibraryFile {
  return {
    Id: String(file.Id ?? file.id ?? ''),
    Title: String(file.Title ?? file.title ?? ''),
    Extension:
      file.Extension != null
        ? String(file.Extension)
        : file.extension != null
          ? String(file.extension)
          : undefined,
    Filesize:
      file.Filesize != null
        ? Number(file.Filesize)
        : file.filesize != null
          ? Number(file.filesize)
          : undefined,
  };
}

function normalizeLibraryFolder(folder: Record<string, unknown>): LibraryFolder {
  const rawFiles = (folder.Files ?? folder.files ?? []) as Record<string, unknown>[];
  const nested = (folder.folders ?? folder.Folders ?? []) as Record<string, unknown>[];
  return {
    Id: String(folder.Id ?? folder.id ?? ''),
    Title: String(folder.Title ?? folder.title ?? ''),
    Files: rawFiles.map(normalizeLibraryFile),
    folders: nested.map(normalizeLibraryFolder),
  };
}

function normalizeLibraryPortfolio(portfolio: Record<string, unknown>): LibraryPortfolio {
  const rawFolders = (portfolio.folders ?? portfolio.Folders ?? []) as Record<string, unknown>[];
  const access = portfolio.access ?? portfolio.Access;
  const canUpload = portfolio.canUpload ?? portfolio.CanUpload;
  return {
    id: String(portfolio.id ?? portfolio.Id ?? ''),
    title: String(portfolio.title ?? portfolio.Title ?? ''),
    clientId: portfolio.clientId != null ? String(portfolio.clientId) : undefined,
    access: access != null ? String(access) : undefined,
    canUpload: typeof canUpload === 'boolean' ? canUpload : undefined,
    folders: rawFolders.map(normalizeLibraryFolder),
  };
}

export async function getPortfolioListByUserAndClient(
  clientId: string,
  userSub: string
): Promise<LibraryPortfolio[]> {
  const response = await axiosInstance.get<LibraryPortfolio[]>(
    `GetPortfolioListByUserAndClient/${clientId}/${userSub}`
  );
  const portfolios = response.data ?? [];
  return portfolios.map((portfolio) =>
    normalizeLibraryPortfolio(portfolio as unknown as Record<string, unknown>)
  );
}

/**
 * Lean portfolio/folder/file list for Add Evidence pickers
 * (id + title only — no ACL / metadata payloads).
 */
export async function getLibraryPickerPortfolios(
  clientId: string,
  userSub: string
): Promise<LibraryPortfolio[]> {
  const response = await axiosInstance.get<LibraryPortfolio[]>(
    `GetLibraryPickerPortfolios/${clientId}/${encodeURIComponent(userSub)}`
  );
  const portfolios = response.data ?? [];
  return portfolios.map((portfolio) =>
    normalizeLibraryPortfolio(portfolio as unknown as Record<string, unknown>)
  );
}

export async function createFileLink(link: LibraryLink): Promise<CreateFileLinksResult> {
  return createFileLinks([link]);
}

/** Create multiple FileLinks in a single API call. */
export async function createFileLinks(links: LibraryLink[]): Promise<CreateFileLinksResult> {
  if (links.length === 0) {
    return { created: 0, failed: 0, failures: [] };
  }

  const response = await axiosInstance.post<CreateFileLinksResult>("CreateFileLinks", links);
  const data = response.data ?? {};
  return {
    created: Number(data.created ?? 0),
    failed: Number(data.failed ?? 0),
    failures: Array.isArray(data.failures) ? data.failures : [],
    links: Array.isArray(data.links) ? data.links : undefined,
  };
}

export async function uploadFolioFile(
  portfolioId: string,
  folderId: string,
  file: File
): Promise<LibraryFileCollection> {
  const extension = file.name.includes(".")
    ? file.name.slice(file.name.lastIndexOf("."))
    : "";

  const formData = new FormData();
  formData.append("reference", portfolioId);
  formData.append("folder", folderId);
  formData.append("extension", extension);
  formData.append("file", file);

  const response = await axiosInstance.post<LibraryFileCollection>("folio/save", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
}

export async function updatePortfolio(portfolio: LibraryPortfolio): Promise<void> {
  await axiosInstance.put("UpdatePortfolio", portfolio);
}
